import React from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Grid2, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import {
  formatOpsTatCount,
  formatOpsTatDays,
  formatOpsTatPercent,
  formatOpsTatSignedDays,
} from "./opsTatFormat";
import OpsTatHowToRead from "./OpsTatHowToRead";

interface OpsTatDispatchBlockProps {
  store: NewOperationsDashboardStore;
}

const OpsTatInlineState = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="8rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

const DispatchStat = ({ label, value }: { label: string; value: string }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.1,
        borderRadius: 2,
        borderColor: customColors.softCardBorder,
        bgcolor: customColors.softCardBackground,
        height: "100%",
      }}
    >
      <CustomTypography
        variant="caption"
        color={customColors.greyTextColor}
        sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="heading7"
        color={customColors.navyText}
        sx={{ mt: 0.6 }}
      >
        {value}
      </CustomTypography>
    </Paper>
  );
};

const OpsTatDispatchBlock: React.FC<OpsTatDispatchBlockProps> = observer(
  ({ store }) => {
    const theme = useTheme();
    const customColors = theme.palette.customColors;
    const dispatch = store.summaryResult?.data.dispatch;

    const openDispatch = async () => {
      try {
        await store.openDrawer("dispatch_deviation", {
          cardTitle: "Dispatch deviation",
          cardSection: "dispatch",
        });
      } catch (error) {
        // drilldownError is set on the store
      }
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
      try {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          void openDispatch();
        }
      } catch (error) {
        // ignore keyboard handler failures
      }
    };

    const renderBody = () => {
      if (store.summaryError && !dispatch) {
        return <OpsTatInlineState message={store.summaryError} />;
      }

      if (store.summaryLoading && !dispatch) {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="8rem"
          >
            <CircularProgress size={28} />
          </Box>
        );
      }

      if (!dispatch) {
        return (
          <OpsTatInlineState message="No dispatch deviation data for the current filters." />
        );
      }

      return (
        <Paper
          variant="outlined"
          role="button"
          tabIndex={0}
          onClick={() => {
            void openDispatch();
          }}
          onKeyDown={onKeyDown}
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: "divider",
            cursor: "pointer",
            "&:hover": {
              borderColor: customColors.purpleMain,
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <CustomTypography
            variant="caption"
            color={customColors.greyTextColor}
            sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
          >
            Dispatch tail
          </CustomTypography>
          <CustomTypography
            variant="heading5"
            color={customColors.navyText}
            sx={{ mt: 0.5, mb: 1.5 }}
          >
            Ready vs expected dispatch
          </CustomTypography>

          <Grid2 container spacing={1.5} columns={12}>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="Deviation mean"
                value={formatOpsTatSignedDays(dispatch.deviation?.mean)}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="Deviation p50"
                value={formatOpsTatSignedDays(dispatch.deviation?.p50)}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="Deviation p90"
                value={formatOpsTatDays(dispatch.deviation?.p90)}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="On-time"
                value={formatOpsTatPercent(
                  dispatch.ready_on_or_before_expected_pct,
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="Not ready past expected"
                value={formatOpsTatCount(dispatch.not_ready_past_expected)}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="Measured"
                value={formatOpsTatCount(
                  dispatch.measured ?? dispatch.orders_ready,
                )}
              />
            </Grid2>
            <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
              <DispatchStat
                label="In flight"
                value={formatOpsTatCount(dispatch.in_flight)}
              />
            </Grid2>
          </Grid2>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              mt: 1.5,
            }}
          >
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              {formatOpsTatCount(dispatch.measured ?? dispatch.orders_ready)}{" "}
              measured · {formatOpsTatCount(dispatch.in_flight)} in flight
            </CustomTypography>
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
            >
              Late {formatOpsTatPercent(dispatch.ready_after_expected_pct)}
            </CustomTypography>
          </Box>

          {dispatch.note ? (
            <CustomTypography
              variant="caption"
              color={customColors.greyTextColor}
              sx={{ mt: 1 }}
            >
              {dispatch.note}
            </CustomTypography>
          ) : null}
        </Paper>
      );
    };

    return (
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
          position: "relative",
        }}
      >
        {store.summaryLoading && dispatch ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.45)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : null}

        <CustomTypography variant="heading3" sx={{ mb: 0.5 }}>
          Dispatch
        </CustomTypography>
        <CustomTypography
          variant="body2"
          color={customColors.greyTextColor}
          sx={{ mb: 1 }}
        >
          Expected dispatch versus when the order became ready. Not a duration
          stage — excluded from the cycle total and spine.
        </CustomTypography>

        <OpsTatHowToRead
          section="dispatch"
          filters={store.appliedFilters}
          items={[
            "Signed days = expected dispatch date − ready_at. + = ready early. − = ready late. This is not duration versus SLA.",
            "Mean is the typical gap. P90 is the high (early) side of that signed series, not the slow tail.",
            "On-time and late are shares of measured orders. Not ready past expected are still in-flight and already past the dispatch date.",
            "Include running changes mean / p50 / p90 / on-time by treating overdue-open orders as ready now. Measured, in flight, and not-ready counts stay the same.",
          ]}
        />

        {renderBody()}
      </Paper>
    );
  },
);

export default OpsTatDispatchBlock;
